import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useEffect, useMemo, useContext, useRef } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import { BottomSheetPageIdProvider } from '../../providers/BottomSheetPageIdProvider';
import { useDevice } from '../../providers/Device';
import { useIsInBottomSheet } from '../../providers/IsInBottomSheetProvider';
import { OverlayContext } from '../../providers/OverlayContextProvider';
import { useTheme } from '../../providers/Theme';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { useIsMounted } from '../../utils/useIsMounted';
import { useStateInRef } from '../../utils/useStateInRef';
import { useBiContextGetter } from '../BiTracking/BiContext';
import { BottomSheet } from '../BottomSheet';
import { OverlayContainerProvider } from '../BottomSheet/BottomSheetContainerContext';
import { BottomSheetContentLayout } from '../BottomSheet/BottomSheetContentLayout';
import { ConfirmationModal } from '../ConfirmationModal';
import { Toast } from '../Toast';
import { GlobalLoader } from './GlobalLoader';
import { ModalContainer } from './ModalContainer';
import styles from './OverlayContext.module.scss';
export function OverlayProvider(props) {
    var _ref, _ref1;
    var _bottomSheetPages_, _bottomSheetPages_1;
    const { children, ModalWrapper, isTestMode = false, disableEnforceFocus, isContainer } = props;
    const [container, setContainer] = useState(null);
    const isMounted = useIsMounted();
    const [modals, setModals] = useState([]);
    const openModals = modals.filter((m)=>m.isOpen);
    const getOpenModals = useStateInRef(openModals);
    const [loaders, setLoaders] = useState({});
    const [toasts, setToasts] = useState([]);
    const [bottomSheetPages, setBottomSheetPages] = useState([]);
    const [bottomSheetIsClosing, setBottomSheetIsClosing] = useState(false);
    const bottomSheetPagesRef = useRef([]);
    const bottomSheetIsClosingRef = useRef(false);
    const currentBottomSheetPage = bottomSheetPages[bottomSheetPages.length - 1];
    const somethingIsLoading = Object.values(loaders).some((count)=>count > 0);
    const shouldBlurBackground = modals.some((modal)=>modal.shouldBlurBackground);
    useEffect(()=>{
        return ()=>{
            setToasts((prevToasts)=>{
                prevToasts.forEach((toast)=>clearTimeout(toast.timer));
                return prevToasts;
            });
        };
    }, []);
    const staticMethods = useMemo(()=>{
        function notifyRemoved(pages) {
            pages.forEach((page)=>{
                var _page_onRemoved;
                return (_page_onRemoved = page.onRemoved) === null || _page_onRemoved === void 0 ? void 0 : _page_onRemoved.call(page);
            });
        }
        function getHasOpenBottomSheet() {
            return bottomSheetPagesRef.current.length > 0 && !bottomSheetIsClosingRef.current;
        }
        function setPages(next) {
            bottomSheetPagesRef.current = next;
            setBottomSheetPages(next);
        }
        function popBottomSheetPage() {
            const prev = bottomSheetPagesRef.current;
            notifyRemoved(prev.slice(-1));
            setPages(prev.length > 1 ? prev.slice(0, -1) : []);
        }
        function removeBottomSheetPage(id) {
            const prev = bottomSheetPagesRef.current;
            notifyRemoved(prev.filter((page)=>page.id === id));
            setPages(prev.filter((page)=>page.id !== id));
        }
        function hideBottomSheet() {
            bottomSheetIsClosingRef.current = true;
            setBottomSheetIsClosing(true);
        }
        function withPageId(config) {
            var _config_id;
            return _object_spread_props(_object_spread({}, config), {
                id: (_config_id = config.id) !== null && _config_id !== void 0 ? _config_id : generateUniqueId('bottom-sheet-page')
            });
        }
        function pushBottomSheetPage(config) {
            setPages([
                ...bottomSheetPagesRef.current,
                withPageId(config)
            ]);
        }
        function showBottomSheet(config) {
            notifyRemoved(bottomSheetPagesRef.current);
            bottomSheetIsClosingRef.current = false;
            setBottomSheetIsClosing(false);
            setPages([
                withPageId(config)
            ]);
        }
        function showToast(content) {
            let isMobileDevice = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
            const defaultDuration = 5;
            const id = "toast".concat(new Date().getTime());
            const transitionDuration = "".concat(defaultDuration * 0.95, "s");
            const timer = setTimeout(()=>{
                isMounted() && setToasts((prevToasts)=>prevToasts.filter((toast)=>toast.id !== id));
            }, defaultDuration * 1000);
            setToasts((prevToasts)=>prevToasts.concat({
                    content,
                    id,
                    transitionDuration,
                    timer,
                    isMobileDevice
                }));
        }
        function showPersistentToast(content) {
            const id = generateUniqueId('persistent-toast');
            setToasts((prevToasts)=>prevToasts.concat({
                    content,
                    id,
                    transitionDuration: '0.2s',
                    timer: 0,
                    isMobileDevice: false,
                    persistent: true
                }));
            return ()=>{
                if (!isMounted()) return;
                setToasts((prevToasts)=>prevToasts.filter((toast)=>toast.id !== id));
            };
        }
        const showModal = (params)=>{
            return new Promise((resolve)=>{
                setModals((prevModals)=>{
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const newModal = _object_spread_props(_object_spread({}, params), {
                        id: generateUniqueId('modal'),
                        isOpen: true,
                        resolve,
                        dismissModal: ()=>{}
                    });
                    return prevModals.concat(newModal);
                });
            });
        };
        const showConfirmAction = async (args)=>{
            const { testId, onConfirm, title, content, primaryButtonLabel, secondaryButtonLabel, textAlignCenter = false, width, skin, openBottomSheetOnMobile, hideBottomSheetOnDismiss } = args;
            const biProps = args.noBi ? {
                noBi: args.noBi
            } : {
                biName: args.biName,
                biData: args.biData
            };
            await showModal(_object_spread_props(_object_spread({}, biProps), {
                closeOnClickOutside: true,
                closeOnEscapeKey: true,
                content: (param)=>{
                    let { dismissModal } = param;
                    return /*#__PURE__*/ _jsx(ConfirmationModal, {
                        // eslint-disable-next-line custom/no-hardcoded-nobi-in-seagull -- modal BI is opt-in via showModal/showConfirmAction; absent biName => no tracking by design
                        noBi: true,
                        testId: testId,
                        skin: skin,
                        title: title,
                        content: content,
                        openBottomSheetOnMobile: openBottomSheetOnMobile,
                        hideBottomSheetOnDismiss: hideBottomSheetOnDismiss,
                        textAlignCenter: textAlignCenter,
                        width: width,
                        primaryButtonLabel: primaryButtonLabel,
                        secondaryButtonLabel: secondaryButtonLabel,
                        onPrimaryButtonClick: async ()=>{
                            await (onConfirm === null || onConfirm === void 0 ? void 0 : onConfirm());
                            dismissModal(true);
                        },
                        onSecondaryButtonClick: ()=>dismissModal(false)
                    });
                }
            }));
        };
        return {
            getHasOpenBottomSheet,
            getOpenedModals () {
                return getOpenModals();
            },
            showModal,
            showConfirmAction,
            showToast,
            hideModal (value) {
                const openModals = getOpenModals();
                if (!openModals.length) return;
                openModals[openModals.length - 1].dismissModal(value);
            },
            hideAllModals (value) {
                for (const modal of getOpenModals()){
                    modal.dismissModal(value);
                }
            },
            startLoader: (name)=>{
                const actualName = name || 'undefined';
                setLoaders((prevLoaders)=>_object_spread_props(_object_spread({}, prevLoaders), {
                        [actualName]: (prevLoaders[actualName] || 0) + 1
                    }));
            },
            stopLoader: (name)=>{
                const actualName = name || 'undefined';
                setLoaders((prevLoaders)=>_object_spread_props(_object_spread({}, prevLoaders), {
                        [actualName]: Math.max(0, (prevLoaders[actualName] || 0) - 1)
                    }));
            },
            showErrorToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "error",
                    text: text,
                    testId: testId
                })),
            showSuccessToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "success",
                    text: text,
                    testId: testId
                })),
            showWarningToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "warning",
                    text: text,
                    testId: testId
                })),
            showInfoToast: (text, testId)=>showToast(/*#__PURE__*/ _jsx(Toast, {
                    type: "info",
                    text: text,
                    testId: testId
                })),
            showPersistentToast,
            showBottomSheet,
            pushBottomSheetPage,
            popBottomSheetPage,
            removeBottomSheetPage,
            hideBottomSheet
        };
    }, []);
    const contextValue = useMemo(()=>_object_spread_props(_object_spread({}, staticMethods), {
            isTestMode
        }), [
        isTestMode,
        staticMethods
    ]);
    function onModalExited(id) {
        setModals((prevModals)=>prevModals.filter((m)=>m.id !== id));
    }
    function triggerClose(id) {
        setModals((prevModals)=>prevModals.map((m)=>m.id === id ? _object_spread_props(_object_spread({}, m), {
                    isOpen: false
                }) : m));
    }
    return /*#__PURE__*/ _jsx(OverlayContext.Provider, {
        value: contextValue,
        children: /*#__PURE__*/ _jsxs(OverlayContainerProvider, {
            element: container,
            children: [
                isContainer ? /*#__PURE__*/ _jsxs("div", {
                    className: styles.overlayContainerScope,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.overlayContext, {
                                [styles.blur]: shouldBlurBackground
                            }),
                            children: children
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            ref: setContainer,
                            className: styles.overlayContainerRoot,
                            "data-testid": "overlay-container-root"
                        })
                    ]
                }) : /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.overlayContext, {
                        [styles.blur]: shouldBlurBackground
                    }),
                    children: children
                }),
                modals.map((modal)=>/*#__PURE__*/ _jsx(ModalContainer, {
                        modal: modal,
                        ModalWrapper: ModalWrapper,
                        isTestMode: isTestMode,
                        disableEnforceFocus: disableEnforceFocus,
                        triggerClose: triggerClose,
                        onModalExited: onModalExited
                    }, modal.id)),
                /*#__PURE__*/ _jsx("div", {
                    className: cn(styles.toastsContainer, {
                        [styles.mobileToastsContainer]: toasts.some((t)=>t.isMobileDevice)
                    }),
                    children: toasts.map((param)=>{
                        let { id, content, transitionDuration, isMobileDevice, persistent } = param;
                        return /*#__PURE__*/ _jsx("div", {
                            className: cn(styles.toast, {
                                [styles.mobileToast]: isMobileDevice,
                                [styles.persistentToast]: persistent
                            }),
                            style: {
                                '--toastDuration': transitionDuration
                            },
                            children: content
                        }, id);
                    })
                }),
                /*#__PURE__*/ _jsx(BottomSheet, {
                    open: bottomSheetPages.length > 0 && !bottomSheetIsClosing,
                    showBackButton: bottomSheetPages.length > 1,
                    paddingBottom: currentBottomSheetPage === null || currentBottomSheetPage === void 0 ? void 0 : currentBottomSheetPage.paddingBottom,
                    fullHeight: (_ref = currentBottomSheetPage === null || currentBottomSheetPage === void 0 ? void 0 : currentBottomSheetPage.fullHeight) !== null && _ref !== void 0 ? _ref : (_bottomSheetPages_ = bottomSheetPages[0]) === null || _bottomSheetPages_ === void 0 ? void 0 : _bottomSheetPages_.fullHeight,
                    variant: (_ref1 = currentBottomSheetPage === null || currentBottomSheetPage === void 0 ? void 0 : currentBottomSheetPage.variant) !== null && _ref1 !== void 0 ? _ref1 : (_bottomSheetPages_1 = bottomSheetPages[0]) === null || _bottomSheetPages_1 === void 0 ? void 0 : _bottomSheetPages_1.variant,
                    onClose: ()=>{
                        staticMethods.hideBottomSheet();
                    },
                    onBack: staticMethods.popBottomSheetPage,
                    onExited: ()=>{
                        const closedPages = bottomSheetPagesRef.current;
                        bottomSheetPagesRef.current = [];
                        setBottomSheetPages([]);
                        bottomSheetIsClosingRef.current = false;
                        setBottomSheetIsClosing(false);
                        closedPages.forEach((page)=>{
                            var _page_onRemoved, _page_onClose;
                            (_page_onRemoved = page.onRemoved) === null || _page_onRemoved === void 0 ? void 0 : _page_onRemoved.call(page);
                            (_page_onClose = page.onClose) === null || _page_onClose === void 0 ? void 0 : _page_onClose.call(page);
                        });
                    },
                    children: bottomSheetPages.map((page, idx)=>/*#__PURE__*/ _jsx("div", {
                            style: {
                                display: idx === bottomSheetPages.length - 1 ? 'contents' : 'none'
                            },
                            children: /*#__PURE__*/ _jsx(BottomSheetPageIdProvider, {
                                value: page.id,
                                children: page.layout != null ? /*#__PURE__*/ _jsx(BottomSheetContentLayout, _object_spread_props(_object_spread({}, page.layout), {
                                    children: page.content
                                })) : page.content
                            })
                        }, page.id))
                }),
                somethingIsLoading && /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ _jsx(GlobalLoader, {
                    scoped: container != null
                }), container !== null && container !== void 0 ? container : document.body)
            ]
        })
    });
}
export function useOverlayContext() {
    const theme = useTheme();
    const getDevice = useStateInRef(useDevice());
    const isInBottomSheet = useIsInBottomSheet();
    const getIsMobileMode = useStateInRef(getDevice().isMobileDevice || isInBottomSheet);
    const internalContext = useContext(OverlayContext);
    const getBiContext = useBiContextGetter();
    return useMemo(()=>{
        const { showModal, showToast, showErrorToast: _showErrorToast, showSuccessToast: _showSuccessToast, showWarningToast: _showWarningToast, showInfoToast: _showInfoToast } = internalContext, rest = _object_without_properties(internalContext, [
            "showModal",
            "showToast",
            "showErrorToast",
            "showSuccessToast",
            "showWarningToast",
            "showInfoToast"
        ]);
        function showTypedToast(type, text, testId) {
            const isMobileMode = getIsMobileMode();
            showToast(/*#__PURE__*/ _jsx(Toast, {
                type: type,
                text: text,
                testId: testId,
                isMobileDevice: isMobileMode
            }), isMobileMode);
        }
        return _object_spread_props(_object_spread({}, rest), {
            showModal: (config)=>{
                var _config_biCallerContext;
                return showModal(_object_spread_props(_object_spread({}, config), {
                    theme,
                    device: getDevice(),
                    biCallerContext: (_config_biCallerContext = config.biCallerContext) !== null && _config_biCallerContext !== void 0 ? _config_biCallerContext : getBiContext
                }));
            },
            showErrorToast: (text, testId)=>showTypedToast('error', text, testId),
            showSuccessToast: (text, testId)=>showTypedToast('success', text, testId),
            showWarningToast: (text, testId)=>showTypedToast('warning', text, testId),
            showInfoToast: (text, testId)=>showTypedToast('info', text, testId)
        });
    }, [
        internalContext,
        theme,
        getBiContext
    ]);
}
